import { MotionConfig } from "motion/react";
import { PostHog } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { env } from "@/env";
import { usePageViewAnalytics } from "@/hooks/use-page-view-analytics";
import { AnalyticsReadyContext } from "@/lib/analytics";
import {
	ANALYTICS_CONSENT_AT_KEY,
	ANALYTICS_CONSENT_CHANGED_EVENT,
	readAnalyticsConsent,
	useAnalyticsConsentState,
} from "@/lib/analytics-consent";
import {
	LIMITED_ANALYTICS_OPTIONS,
	restrictAnalyticsEvent,
} from "@/lib/analytics-payload";
import { clearAnalyticsStorage } from "@/lib/analytics-storage";
import { useI18n } from "@/lib/i18n/use-i18n";
import ConsentBanner from "./ConsentBanner";
import Header from "./Header";
import SiteFooter from "./SiteFooter";

const CONTENT_ID = "content";

export function RootShell({ children }: { children: ReactNode }) {
	const { t, locale } = useI18n();
	usePageViewAnalytics(locale);

	return (
		<MotionConfig reducedMotion="user">
			<a
				href={`#${CONTENT_ID}`}
				className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:bg-ink focus:px-3 focus:py-2 focus:font-mono focus:text-sm focus:text-paper"
			>
				{t((t) => t.nav.skip)}
			</a>
			<div className="site-shell flex min-h-screen flex-col">
				<Header />
				<main id={CONTENT_ID} className="flex-1">
					{children}
				</main>
				<SiteFooter />
			</div>
			{env.VITE_ANALYTICS_ENABLED && env.VITE_POSTHOG_KEY ? (
				<ConsentBanner />
			) : null}
		</MotionConfig>
	);
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
	const enabled = env.VITE_ANALYTICS_ENABLED && Boolean(env.VITE_POSTHOG_KEY);
	useEffect(() => {
		if (!enabled) clearAnalyticsStorage({ clearConsent: true });
	}, [enabled]);
	if (!enabled || !env.VITE_POSTHOG_KEY) return <>{children}</>;
	return (
		<ConsentedAnalyticsProvider apiKey={env.VITE_POSTHOG_KEY}>
			{children}
		</ConsentedAnalyticsProvider>
	);
}

export function ConsentedAnalyticsProvider({
	apiKey,
	children,
}: {
	apiKey: string;
	children: ReactNode;
}) {
	const [client] = useState(() => new PostHog());
	const [ready, setReady] = useState(false);
	const initialized = useRef(false);
	const activeDecision = useRef<string | null>(null);
	const state = useAnalyticsConsentState();

	useEffect(() => {
		if (state === "loading") return;
		const sync = () => {
			if (readAnalyticsConsent() !== "granted") {
				if (initialized.current && activeDecision.current !== null) {
					client.opt_out_capturing();
					client.reset(true);
				}
				activeDecision.current = null;
				clearAnalyticsStorage();
				setReady(false);
				return;
			}
			if (!initialized.current) {
				clearAnalyticsStorage();
				client.init(apiKey, {
					...LIMITED_ANALYTICS_OPTIONS,
					api_host: env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com",
					before_send: (event) =>
						readAnalyticsConsent() === "granted"
							? restrictAnalyticsEvent(event)
							: null,
				});
				initialized.current = true;
			}
			const decision = localStorage.getItem(ANALYTICS_CONSENT_AT_KEY);
			if (decision !== activeDecision.current) {
				client.opt_in_capturing({ captureEventName: false });
				activeDecision.current = decision;
			}
			setReady(client.has_opted_in_capturing());
		};
		sync();
		window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sync);
		window.addEventListener("storage", sync);
		return () => {
			window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sync);
			window.removeEventListener("storage", sync);
		};
	}, [apiKey, client, state]);

	useEffect(
		() => () => {
			if (initialized.current) {
				client.opt_out_capturing();
				client.reset(true);
				activeDecision.current = null;
				clearAnalyticsStorage();
			}
		},
		[client],
	);

	return (
		<PostHogProvider client={client}>
			<AnalyticsReadyContext.Provider value={ready}>
				{children}
			</AnalyticsReadyContext.Provider>
		</PostHogProvider>
	);
}
